import { useEffect, useMemo, useState } from 'react';
import { loadCharacterData } from '../lib/characterData';
import { mergeStoredState, readJson, writeJson } from '../lib/storage';
import { buildShareUrl } from '../lib/shareState';

const FILTER_KEYS = ['role', 'element', 'race', 'affiliation'];

function splitAffiliations(value) {
  if (!value) {
    return [];
  }

  return String(value)
    .split(/[/／]+/u)
    .map((v) => v.trim())
    .filter(Boolean);
}

function splitRaces(value) {
  if (!value) {
    return [];
  }

  return String(value)
    .split(/[/／]+/u)
    .map((v) => String(v).replace(/^セカンド種族[:：]\s*/u, '').trim())
    .filter(Boolean);
}

function parseDateKey(value) {
  if (!value) return '';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  return `${m[1]}${m[2]}${m[3]}`;
}

function sortByKey(items, sortBy, sortOrder) {
  const copied = [...items];
  const direction = sortOrder === 'desc' ? -1 : 1;

  if (sortBy === 'name') {
    copied.sort((a, b) => {
      const byName = String(a.name).localeCompare(String(b.name), 'ja');
      if (byName !== 0) return byName * direction;
      return (Number(a.id) - Number(b.id)) * direction;
    });
    return copied;
  }

  copied.sort((a, b) => {
    const da = parseDateKey(a.implementation_date);
    const db = parseDateKey(b.implementation_date);

    if (da && db && da !== db) {
      return da < db ? -1 * direction : 1 * direction;
    }
    if (da && !db) {
      return -1;
    }
    if (!da && db) {
      return 1;
    }

    return (Number(a.id) - Number(b.id)) * direction;
  });
  return copied;
}

export function useCatalogState(storagePrefix, options = {}) {
  const { persist = true, sharedMap = null } = options;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [skillSearchText, setSkillSearchText] = useState('');
  const [sortBy, setSortBy] = useState('implementation');
  const [sortOrder, setSortOrder] = useState('asc');
  const [possessionFilter, setPossessionFilter] = useState('all');
  const [filters, setFilters] = useState({
    role: [],
    element: [],
    race: [],
    affiliation: [],
  });

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      const base = await loadCharacterData();

      if (!active) {
        return;
      }

      const merged = base.map((character) => {
        const stored = persist ? readJson(`${storagePrefix}-${character.id}`, null) : null;
        return mergeStoredState(character, stored);
      });

      let nextItems = merged;
      if (sharedMap) {
        nextItems = merged.map((item) => {
          const id = String(item.id);
          const has = sharedMap.has(id);
          const rarity = has ? sharedMap.get(id) : '';
          return { ...item, possessed: has, rarity };
        });
      }

      setItems(sortByKey(nextItems, 'implementation', 'asc'));
      setLoading(false);
    }

    load().catch(() => {
      if (active) {
        setLoading(false);
      }
    });

    return () => {
      active = false;
    };
  }, [storagePrefix, persist, sharedMap]);

  const filterOptions = useMemo(() => {
    const result = { role: [], element: [], race: [], affiliation: [] };

    FILTER_KEYS.forEach((key) => {
      const values = new Set();
      items.forEach((item) => {
        if (!item[key]) {
          return;
        }

        if (key === 'affiliation') {
          splitAffiliations(item[key]).forEach((aff) => values.add(aff));
          return;
        }
        if (key === 'race') {
          splitRaces(item[key]).forEach((race) => values.add(race));
          return;
        }

        values.add(item[key]);
      });
      result[key] = [...values];
    });

    return result;
  }, [items]);

  const filteredItems = useMemo(() => {
    let result = items;

    if (searchText) {
      const q = searchText.trim();
      result = result.filter((item) => item.name.includes(q) || item.title.includes(q));
    }

    if (skillSearchText) {
      const q = skillSearchText.trim();
      result = result.filter((item) => {
        const targets = [
          item.s1,
          item.s2,
          item.s1_plus,
          item.s2_plus,
          item.unique_ability,
          item.unique_skill,
          item.accessory_effect,
        ];
        return targets.some((text) => String(text || '').includes(q));
      });
    }

    FILTER_KEYS.forEach((key) => {
      if (filters[key].length > 0) {
        if (key === 'affiliation') {
          result = result.filter((item) => {
            const itemAffiliations = splitAffiliations(item.affiliation);
            return filters.affiliation.some((selected) => itemAffiliations.includes(selected));
          });
        } else if (key === 'race') {
          result = result.filter((item) => {
            const itemRaces = splitRaces(item.race);
            return filters.race.some((selected) => itemRaces.includes(selected));
          });
        } else {
          result = result.filter((item) => filters[key].includes(item[key]));
        }
      }
    });

    if (possessionFilter === 'possessed') {
      result = result.filter((item) => Boolean(item.possessed));
    }

    if (possessionFilter === 'notPossessed') {
      result = result.filter((item) => !item.possessed);
    }

    return sortByKey(result, sortBy, sortOrder);
  }, [filters, items, possessionFilter, searchText, skillSearchText, sortBy, sortOrder]);

  const toggleFilter = (key, value) => {
    setFilters((prev) => {
      const selected = prev[key] || [];
      const exists = selected.includes(value);
      return {
        ...prev,
        [key]: exists ? selected.filter((v) => v !== value) : [...selected, value],
      };
    });
  };

  const cyclePossessionFilter = () => {
    setPossessionFilter((prev) => {
      if (prev === 'all') {
        return 'possessed';
      }
      if (prev === 'possessed') {
        return 'notPossessed';
      }
      return 'all';
    });
  };

  const updateItem = (id, updater) => {
    setItems((prev) => {
      const next = prev.map((item) => {
        if (item.id !== id) {
          return item;
        }

        const updated = updater(item);
        if (persist) {
          writeJson(`${storagePrefix}-${id}`, updated);
        }
        return updated;
      });

      return next;
    });
  };

  const togglePossession = (id) => {
    updateItem(id, (item) => ({ ...item, possessed: !item.possessed }));
  };

  const setRarity = (id, rarity) => {
    updateItem(id, (item) => ({ ...item, rarity }));
  };

  return {
    loading,
    items: filteredItems,
    searchText,
    setSearchText,
    skillSearchText,
    setSkillSearchText,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    possessionFilter,
    cyclePossessionFilter,
    filters,
    toggleFilter,
    filterOptions,
    togglePossession,
    setRarity,
    createShareUrl: () => (storagePrefix === 'character' ? buildShareUrl(items) : ''),
  };
}
